/**
 * 注入上下文信息条（T24）。
 *
 * 助手消息下方的一行可展开提示，说明**本次请求实际注入了什么**
 * （「已注入上下文：角色卡 X · 世界书 Y 条 · 记忆 Z 条 · 搜索 W 条」）；
 * 展开后按固定顺序列出逐来源计数。只报计数与来源，不展示注入全文。
 *
 * 与对话页其它部件一致：可见节点用 `useComponentMeta()` 登记 390x844 锚点；
 * 子项锚点相对宿主气泡布局换算，父级气泡布局由调用方传入。
 */

import { motion } from 'framer-motion';
import { useState } from 'react';
import type { ReactElement } from 'react';

import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import type { ContextInjectionSummary } from './contextAssembly';
import { describeInjectionSources, formatInjectionSummary } from './contextAssembly';
import styles from './ChatPage.module.css';

export interface ContextNoticeProps {
  /** 宿主消息气泡的登记 id 前缀，如 `chat.message.assistant.<id>`。 */
  baseId: string;
  /** 宿主气泡在 390x844 画布上的声明布局（用于换算锚点）。 */
  layout: BoxLayout;
  summary: ContextInjectionSummary;
}

/** 展开明细相对信息条顶部的偏移（dp）。 */
const DETAIL_OFFSET = 44;

/**
 * 注入信息条；`summary.injected` 为 false 时由调用方负责不挂载本组件，
 * 这里仍做一次防御性判断，避免出现「没有任何注入却显示信息条」。
 */
export function ContextNotice({ baseId, layout, summary }: ContextNoticeProps): ReactElement | null {
  const presets = useMotionPresets();
  const [expanded, setExpanded] = useState(false);

  const label = formatInjectionSummary(summary);
  const containerMeta: ComponentMetaInput = {
    id: `${baseId}.context`,
    page: 'chat',
    role: 'container',
    label: '注入上下文信息条',
    layout,
    zIndex: 5,
    props: ['expanded'],
    notes: '助手消息下方的一行「已注入上下文」提示，可展开查看逐来源计数；安卓对应可折叠 CardView',
  };
  const toggleMeta: ComponentMetaInput = {
    id: `${baseId}.context.toggle`,
    page: 'chat',
    role: 'button',
    label: '展开/收起注入上下文',
    layout,
    zIndex: 6,
    props: ['expanded'],
    notes: '点击展开 / 收起注入来源明细；可点高度不小于 44dp，安卓对应 MaterialButton',
  };
  const detailMeta: ComponentMetaInput = {
    id: `${baseId}.context.text`,
    page: 'chat',
    role: 'text',
    label: '注入来源明细',
    layout: { ...layout, y: layout.y + DETAIL_OFFSET },
    zIndex: 5,
    notes: '展开后按「角色卡 / 世界书 / 记忆 / 搜索」顺序逐行显示来源与计数；安卓对应 TextView',
  };

  const containerRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(containerMeta, 'container'));
  const toggleRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(toggleMeta, 'button'));
  const detailRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(detailMeta, 'text'));

  if (!summary.injected || label === '') return null;

  const lines = describeInjectionSources(summary);

  return (
    <div
      ref={containerRef}
      className={styles.contextNotice}
      data-ui-id={containerMeta.id}
      data-ui-role="container"
      data-expanded={expanded ? 'true' : 'false'}
    >
      <motion.button
        ref={toggleRef}
        type="button"
        className={styles.contextToggle}
        data-ui-id={toggleMeta.id}
        data-ui-role="button"
        aria-expanded={expanded}
        aria-label={label}
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className={styles.contextCaret} aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
        <span className={styles.contextLabel}>{label}</span>
      </motion.button>

      {expanded ? (
        <div
          ref={detailRef}
          className={styles.contextDetail}
          data-ui-id={detailMeta.id}
          data-ui-role="text"
        >
          {lines.map((line) => (
            <p key={line} className={styles.contextDetailLine}>
              {line}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
